import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Mail, Phone, Linkedin, Facebook, Instagram, Globe, Users } from 'lucide-react';
import { NetblitzLogo } from '@/components/brand/Logo';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTok = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [quoteCount, setQuoteCount] = useState(0);

  // Fetch quote count
  useEffect(() => {
    const fetchQuoteCount = async () => {
      try {
        const response = await fetch(`${API_URL}/api/quotes/count`);
        if (response.ok) {
          const data = await response.json();
          setQuoteCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching quote count:', error);
      }
    };
    fetchQuoteCount();
  }, []);

  const quoteCounterText = {
    nl: 'offertes aangevraagd via onze chatbot',
    fr: 'devis demandés via notre chatbot',
    de: 'Angebote über unseren Chatbot angefragt',
    en: 'quotes requested via our chatbot'
  };

  const quickLinks = [
    { routeKey: 'services', label: t('nav.services') },
    { routeKey: 'portfolio', label: t('nav.portfolio') },
    { routeKey: 'pricing', label: t('nav.pricing') },
    { routeKey: 'faq', label: t('nav.faq') },
    { routeKey: 'contact', label: t('nav.contact') },
    { routeKey: 'privacy', label: t('footer.privacy') },
    { routeKey: 'terms', label: t('footer.terms') },
    { routeKey: 'serviceDescription', label: language === 'nl' ? 'Dienstbeschrijving' : language === 'en' ? 'Service Description' : language === 'fr' ? 'Description du Service' : 'Dienstbeschreibung' },
  ].map(link => ({
    ...link,
    path: getLocalizedPath(link.routeKey, language)
  }));

  const services = [
    t('footer.service1'),
    t('footer.service2'),
    t('footer.service3'),
    t('footer.service4'),
  ];

  return (
    <footer className="bg-[#0F172A] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <Link to={getLocalizedPath('home', language)} className="flex items-center gap-2 group">
              <NetblitzLogo size={40} className="group-hover:scale-110 transition-transform" />
              <span className="text-2xl font-bold">
                Net<span className="text-[#2563EB]">blitz</span>
              </span>
            </Link>
            <p className="text-[#94a3b8] leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#2563EB] transition-colors"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#2563EB] transition-colors"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#2563EB] transition-colors"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://tiktok.com/@jb51225" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#2563EB] transition-colors"
                aria-label="TikTok"
                data-testid="social-tiktok"
              >
                <TikTok size={18} />
              </a>
            </div>
            <div className="pt-2">
              <LanguageSwitcher variant="footer" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.routeKey}>
                  <Link
                    to={link.path}
                    className="text-[#94a3b8] hover:text-white hover:pl-2 transition-all"
                    data-testid={`footer-link-${link.routeKey}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.servicesTitle')}</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-[#94a3b8]">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold mb-6">{t('footer.contactTitle')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
                <a 
                  href="mailto:hello@netblitz.eu" 
                  className="text-[#94a3b8] hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  hello@netblitz.eu
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
                <div className="text-[#94a3b8]">
                  <a href="tel:+31851155703" className="hover:text-white transition-colors block">
                    NL: 085 115 5703
                  </a>
                  <a href="tel:+32484448854" className="hover:text-white transition-colors block">
                    BE: 0484 448 854
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Globe className="w-5 h-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
                <span className="text-[#94a3b8]">
                  {t('footer.worldwide')}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#94a3b8] text-sm">
              © {currentYear} Netblitz. {t('footer.copyright')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <Link to={getLocalizedPath('privacy', language)} className="text-[#94a3b8] hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to={getLocalizedPath('terms', language)} className="text-[#94a3b8] hover:text-white transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to={getLocalizedPath('serviceDescription', language)} className="text-[#94a3b8] hover:text-white transition-colors">
                {language === 'nl' ? 'Dienstbeschrijving' : language === 'en' ? 'Service Description' : language === 'fr' ? 'Description du Service' : 'Dienstbeschreibung'}
              </Link>
              <Link to="/portal-admin" className="text-[#64748b] hover:text-[#2563EB] transition-colors">
                {t('footer.portal') || 'Portal'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
